import React, { useCallback, useState } from "react";
import Link from "next/link";
import {
  Formik, Form, Field, ErrorMessage,
} from "formik";
import { object, string, boolean } from "yup";
import { withState, compose } from "recompose";
import { isEmpty } from "ramda";
import ReactLoading from "react-loading";
import classNames from "classnames";

import trackingMDE from "utils/analytics/trackingMDE";
import pushToDataLayer from "utils/pushToDataLayer";
import getDealType from "utils/analytics/getDealType";
import getInputErrorClass from "utils/getInputErrorClass";
import { useNavigation } from "contexts/navigation/NavigationContext";
import useCustomerType from "dynamic/hooks/useCustomerType";
import useSession from "dynamic/hooks/useSession";
import { dynamicWithoutLayoutWrapper } from "dynamic/hocs/Dynamic";

import InputPhone from "../dynamic/components/FormComponents/InputPhone";
import InputZipCode from "../dynamic/components/FormComponents/InputZipCode";
import zipCodeSchema from "../dynamic/schemas/contactDealerForm/zipCodeSchema";
import {
  CheckboxSquareChecked,
  CheckboxSquareUnchecked,
} from "./icons/Checkboxes";
import { composeClassicEmail } from 'utils/composeClassicMessage';

const defaultText = {
  button: "Anfrage senden",
};

const CustomErrorMessage = ({ children }) => (
  <div className="text-error text-xs leading-tight mt-1/2">
    {children}
  </div>
);

const ErrorElement = (props) => (
  <CustomErrorMessage>
    <ErrorMessage {...props} component="span" data-cy="messageError" />
    &nbsp;
  </CustomErrorMessage>
)

const ContactFormValidationSchema = (props) => (props.companyRequired
  ? object().shape({
    salutation: string().required("Bitte wählen Sie eine Anrede."),
    firstname: string().required("Dieses Feld ist erforderlich."),
    lastname: string().required("Dieses Feld ist erforderlich."),
    company: string().required(
      "Bitte geben Sie den Namen Ihrer Firma ein.",
    ),
    email: string()
      .email("Bitte geben Sie eine gültige E-Mail Adresse ein.")
      .required("Bitte geben Sie Ihre E-Mail Adresse ein."),
    phoneNumber: string().required(
      "Bitte geben Sie Ihre Telefonnummer an.",
    ),
    ...(props.shouldShowZipCode ? zipCodeSchema : {}),
    message: string().required("Bitte geben Sie Ihre Nachricht ein."),
    confirmBusinessCustomer: boolean().oneOf(
      [true],
      "Sie müssen bestätigen, dass Sie Gewerbetreibender sind.",
    ),
    submitConsent: boolean(),
  })
  : object().shape({
    salutation: string().required("Bitte wählen Sie eine Anrede."),
    firstname: string().required("Dieses Feld ist erforderlich."),
    lastname: string().required("Dieses Feld ist erforderlich."),
    company: string(),
    email: string()
      .email("Bitte geben Sie eine gültige E-Mail Adresse ein.")
      .required("Bitte geben Sie Ihre E-Mail Adresse ein."),
    phoneNumber: string().required(
      "Bitte geben Sie Ihre Telefonnummer an.",
    ),
    ...(props.shouldShowZipCode ? zipCodeSchema : {}),
    message: string().required("Bitte geben Sie Ihre Nachricht ein."),
    submitConsent: boolean(),
  }));

const ContactForm = (props) => {
  const [_, {
    checkoutProcessEnabled,
    selectedProduct,
    selectedLeasingOffer,
    leasingCalculatorEnabled,
  }] = useNavigation();
  const { isAuthenticated } = useSession();
  const [customerType] = useCustomerType();
  const [messageModified, setMessageModified] = useState(false);
  const dealType = getDealType(leasingCalculatorEnabled, checkoutProcessEnabled);
  const {
    buttonText = defaultText.button,
    submitClicked,
    setSubmitClicked,
    hideBorders = false,
  } = props;

  const salutationOptions = [
    { value: "Herr", label: "Herr" },
    { value: "Frau", label: "Frau" },
  ];

  const renderInputPhone = useCallback(
    ({ field }) => <InputPhone {...field} enableErrorMessage={false} containerClass="outline-none focus:border-secondary mt-1" />,
    [],
  );

  const renderZipCode = useCallback(
    ({ field }) => (
      <InputZipCode {...field} className="mt-1" placeholder="PLZ" />
    ),
    [],
  );

  const handleNameChange = (nameType, currentValue, newValue, setFieldValue) => {
    setFieldValue(nameType, newValue, true);

    if (!messageModified) {
      const firstName = nameType === "firstname" ? newValue : currentValue;
      const lastName = nameType === "lastname" ? newValue : currentValue;
      setFieldValue("message", composeClassicEmail(firstName, lastName, props.defaultMessage), true);
    }
  };

  const loadingIcon = (
    <ReactLoading
      type="spin"
      color="#fff"
      width={19}
      height={19}
      className="m-auto"
    />
  );

  const initialValues = {
    salutation: salutationOptions[0].value,
    firstname: "",
    lastname: "",
    company: "",
    email: "",
    phoneNumber: "",
    zipCode: "",
    requestRecall: "",
    message: props.defaultMessage || "",
    confirmBusinessCustomer: false,
    submitConsent: true,
    subscribeToNewsletter: false,
  };

  return (
    <div className="flex flex-col">
      <Formik
        initialValues={initialValues}
        onSubmit={(values, formikBag) => {
          const { setSubmitting } = formikBag;
          const hasSubmitPromise = !!props.onSubmit;
          if (hasSubmitPromise) {
            pushToDataLayer(
              trackingMDE({
                selectedProduct,
                selectedLeasingOffer,
                isAuthenticated,
                customerType,
                deal_type: dealType,
                commission: 20,
                category: "ReplyEmailFlow",
                action: "R2SEmailAttempt",
              }),
            );
            props
              .onSubmit(values, formikBag)
              .then(() => {
                setSubmitting(false);
              })
              .catch(() => {
                setSubmitting(false);
              });
          } else {
            setSubmitting(false);
          }
        }}
        validationSchema={ContactFormValidationSchema(props)}
      >
        {({ values, isSubmitting, errors, touched, setFieldValue }) => (
          <Form className="contact-form flex flex-col">
            <div className={classNames("flex flex-col px-4 md:px-5 pt-2 lg:pt-4 bg-white", {
              "lg:border lg:border-grey lg:border-t-0 lg:border-b-0": !hideBorders,
            })}>
              <span className="text-sm text-dark w-full mb-1.5">Anrede</span>
              <div className="flex">
                {
                  salutationOptions.map(option => (
                    <label className="text-dark cursor-pointer mb-2 mr-6">
                      <Field
                        name="salutation"
                        type="radio"
                        value={option.value}
                        className="mr-2"
                        checked={values.salutation === option.value}
                      />
                      <span className="sibling-checked-font-bold">{option.label}</span>
                    </label>
                  ))
                }
              </div>
              <ErrorElement name="salutation" />

              <div className="flex">
                <label className="text-sm text-dark w-full mb-2 mr-3">
                  Vorname
                  <Field
                    onChange={(e) => handleNameChange("firstname", values.lastname, e.target.value, setFieldValue)}
                    name="firstname"
                    placeholder="Vorname"
                    className={`border border-grey-dark rounded-sm text-lg pl-2 py-3 w-full outline-none focus:border-secondary mt-1 ${getInputErrorClass(errors?.firstname && touched.firstname)}`}
                    data-cy="firstNameField"
                  />
                  <ErrorElement name="firstname" />
                </label>

                <label className="text-sm text-dark w-full mb-2">
                  Nachname
                  <Field
                    onChange={(e) => handleNameChange("lastname", values.firstname, e.target.value, setFieldValue)}
                    name="lastname"
                    placeholder="Nachname"
                    className={`border border-grey-dark rounded-sm text-lg pl-2 py-3 w-full outline-none focus:border-secondary mt-1 ${getInputErrorClass(errors?.lastname && touched.lastname)}`}
                    data-cy="lastNameField"
                  />
                  <ErrorElement name="lastname" />
                </label>
              </div>

              {!props.isPrivateOffer && (
                <>
                  <label className="text-sm text-dark w-full mb-2">
                    {`${props.companyRequired ? "Firma" : "Firma (Optional)"}`}
                    <Field
                      name="company"
                      placeholder={`${props.companyRequired ? "Firma" : "Firma (Optional)"}`}
                      className="border border-grey-dark rounded-sm text-lg pl-2 py-3 w-full outline-none focus:border-secondary mt-1"
                      data-cy="companyField"
                    />
                    <ErrorElement name="company" />
                  </label>
                </>
              )}

              <label className="text-sm text-dark w-full mb-2">
                E-Mail Adresse
                <Field
                  name="email"
                  type="email"
                  placeholder="E-Mail Adresse"
                  className={`border border-grey-dark rounded-sm text-lg pl-2 py-3 w-full outline-none focus:border-secondary mt-1 ${getInputErrorClass(errors?.email && touched.email)}`}
                  data-cy="emailField"
                />
                <ErrorElement name="email" />
              </label>

              {props.shouldShowZipCode ?
                <div className="flex">
                  <label className={`text-sm text-dark w-full mb-2 mr-3 ${getInputErrorClass(errors?.phoneNumber && touched.phoneNumber)}`}>
                    Telefonnummer
                    <Field name="phoneNumber" component={renderInputPhone} />
                    <ErrorElement name="phoneNumber" />
                  </label>
                  <div className="text-sm text-dark w-1/3 mb-2">
                    PLZ
                    <Field name="zipCode" component={renderZipCode} className="w-full" />
                    <ErrorElement name="zipCode" />
                  </div>
                </div>
                :
                <label className={`text-sm text-dark w-full mb-2 ${getInputErrorClass(errors?.phoneNumber && touched.phoneNumber)}`}>
                  Telefonnummer
                  <Field name="phoneNumber" component={renderInputPhone} />
                  <ErrorElement name="phoneNumber" />
                </label>
              }

              <label
                htmlFor="message"
                className="paragraph--small text-dark text-sm"
              >
                Ihre Nachricht
              </label>
              <Field
                onChange={(e) => {
                  setFieldValue("message", e.target.value, true);
                  setMessageModified(true);
                }}
                component="textarea"
                id="message"
                name="message"
                className={`border border-grey-dark rounded-sm h-48 pt-3 pb-5 px-2 paragraph font-sans outline-none focus:border-secondary mt-1 ${getInputErrorClass(errors?.message && touched.message)}`}
              />
              <ErrorElement name="message" />

              <div className="flex pt-6 relative">
                <span className="paragraph--small text-grey-darkest leading-normal">
                  Mit Absenden der Anfrage erklärst Du Dich mit dem Erhalt einer
                  Zufriedenheitsumfrage per E-Mail einverstanden. Für weitere
                  Informationen lies bitte unsere
                  {" "}
                  <Link href="/datenschutz">
                    <a className="link link--secondary paragraph--small">
                      Datenschutzerklärung
                    </a>
                  </Link>
                  .
                </span>
              </div>
              {props.companyRequired && (
                <div className="flex pt-3 relative">
                  <Field
                    type="checkbox"
                    id="confirmBusinessCustomer"
                    name="confirmBusinessCustomer"
                    className="form-controls__input"
                    data-cy="confirmBusinessCustomer"
                  />
                  <div>
                    <label
                      htmlFor="confirmBusinessCustomer"
                      className="paragraph--small text-grey-darkest leading-normal form-controls__label"
                    >
                      <span className="form-controls__checkmark">
                        {values.confirmBusinessCustomer ? (
                          <CheckboxSquareChecked />
                        ) : (
                          <CheckboxSquareUnchecked />
                        )}
                      </span>
                      Dieses Angebot gilt nur für Gewerbekunden: Ich bestätige,
                      dass ich Gewerbetreibender bin.
                    </label>
                    <div className="pl-6">
                      <ErrorElement name="confirmBusinessCustomer" />
                    </div>
                  </div>
                </div>
              )}

              {!props.isPartnerContact && (
                <div className="flex pt-3 relative">
                  <Field
                    type="checkbox"
                    id="subscribeToNewsletter"
                    name="subscribeToNewsletter"
                    className="form-controls__input"
                  />
                  <label
                    htmlFor="subscribeToNewsletter"
                    className="paragraph--small text-grey-darkest leading-normal form-controls__label"
                  >
                    <span className="form-controls__checkmark">
                      {values.subscribeToNewsletter ? (
                        <CheckboxSquareChecked />
                      ) : (
                        <CheckboxSquareUnchecked />
                      )}
                    </span>
                    Ich möchte von Null-Leasing per E-Mail Angebote,
                    Umfragen sowie Informationen über Produkte und Leistungen
                    von Null-Leasing und der mobile.de GmbH erhalten (jederzeit widerrufbar).
                  </label>
                </div>
              )}
            </div>

            <div className={classNames("pt-2 px-4 md:px-5 bg-white", { "lg:border-l lg:border-r lg:border-grey": !hideBorders })} data-cy="messageError">
              <CustomErrorMessage>
                {submitClicked && !isEmpty(errors) && (
                  "Bitte füllen Sie alle Pflichtfelder aus."
                )}
                &nbsp;
              </CustomErrorMessage>
            </div>

            <div
              className={classNames(
                "flex flex-col px-4 md:px-5 bg-white rounded-b-xsm overflow-hidden",
                {
                  "sticky md:static pin-b pin-l pin-r mt-4 md:mt-0 z-10 py-5 pt-2": props.stickySubmitOnMobile,
                  "border-t md:border-t-0": props.stickySubmitOnMobile && !hideBorders,
                  "lg:border lg:border-t-0 lg:border-b-0 lg:border-grey": !hideBorders,
                },
              )}
            >
              <button
                type="submit"
                disabled={isSubmitting}
                className={`btn btn--primary btn--submit paragraph--large ${
                  isSubmitting ? "btn--disabled" : ""
                }`}
                onClick={() => setSubmitClicked(true)}
                data-cy="contactDealerSubmitButton"
              >
                {isSubmitting ? loadingIcon : buttonText}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default compose(
  dynamicWithoutLayoutWrapper,
  withState(
    "submitClicked",
    "setSubmitClicked",
    false,
  ),
)(ContactForm);
